<script setup lang="ts">
import { ref, watch } from 'vue';

const isMenuOpen = ref(false);
const menuButtonRef = ref<HTMLElement | null>(null);
const firstMenuItemRef = ref<HTMLElement | null>(null);

const navLinks = [
    { name: 'Inicio', href: '#', action: 'landing' },
    { name: 'Sorteos Activos', href: '#sorteos', action: 'active_raffles' },
    { name: 'Cómo Jugar', href: '#como-jugar', action: 'how_it_works' },
    { name: 'Contacto', href: '#contacto', action: 'contact' }
];

const emit = defineEmits(['navigate']);

const handleNavInfo = (step: string) => {
    emit('navigate', step);
    isMenuOpen.value = false;
};

const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
    isMenuOpen.value = false;
};

watch(isMenuOpen, (isOpen) => {
    if (isOpen) {
        document.body.style.overflow = 'hidden';
        setTimeout(() => {
            firstMenuItemRef.value?.focus();
        }, 100);
    } else {
        document.body.style.overflow = '';
        menuButtonRef.value?.focus();
    }
});

const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isMenuOpen.value) {
        closeMenu();
    }
};

const handleMenuButtonKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleMenu();
    } else if (e.key === 'ArrowDown' && isMenuOpen.value) {
        e.preventDefault();
        firstMenuItemRef.value?.focus();
    }
};
</script>

<template>
    <nav 
        role="navigation" 
        aria-label="Navegación principal"
        class="sticky top-0 z-50 w-full backdrop-blur-xl bg-black/80 border-b border-white/5 transition-all duration-300"
        @keydown="handleEscapeKey"
    >
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex items-center justify-between h-20">
                <!-- Logo -->
                <div 
                    role="button"
                    tabindex="0"
                    class="flex-shrink-0 cursor-pointer group" 
                    @click="handleNavInfo('landing')"
                    @keydown.enter="handleNavInfo('landing')"
                    @keydown.space.prevent="handleNavInfo('landing')"
                    aria-label="Ir al inicio"
                >
                    <div class="flex items-center gap-3">
                         <!-- Logo Icon -->
                        <img 
                            src="https://media.lafavoritarifas.com/web/logo.png" 
                            alt="Logo La Favorita" 
                            class="w-12 h-12 object-contain hover:scale-110 transition-transform duration-300 drop-shadow-lg"
                            loading="eager"
                        />
                         <!-- Logo Text -->
                        <div class="flex flex-col">
                            <span class="font-bold text-xl tracking-tight text-white leading-none">RIFAS</span>
                            <span class="font-black text-xl tracking-widest text-gold-500 leading-none group-hover:text-gold-400 transition-colors">LA FAVORITA</span>
                        </div>
                    </div>
                </div>

                <!-- Desktop Menu -->
                <div class="hidden md:block" role="menubar" aria-label="Menú principal">
                    <div class="ml-10 flex items-baseline space-x-8">
                        <button 
                            v-for="(link, index) in navLinks" 
                            :key="link.name"
                            :aria-current="link.action === 'landing' ? 'page' : undefined"
                            :aria-label="link.name"
                            role="menuitem"
                            @click="link.action ? handleNavInfo(link.action) : null"
                            class="text-sm font-medium text-gray-300 hover:text-white hover:scale-105 transition-all duration-200 relative group focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-black rounded"
                        >
                            {{ link.name }}
                            <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-gold-500 transition-all duration-300 group-hover:w-full"></span>
                        </button>
                    </div>
                </div>

                <!-- Action Button (Desktop) -->
                <div class="hidden md:block">
                     <button
                        @click="handleNavInfo('my_tickets')" 
                        aria-label="Ver mis tickets"
                        class="px-6 py-2.5 rounded-full bg-white/5 border border-white/10 hover:bg-gold-500 hover:border-gold-500 hover:text-black hover:shadow-[0_0_20px_rgba(245,158,11,0.3)] transition-all duration-300 font-bold text-sm text-gray-300 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-black"
                    >
                        Mis Tickets
                    </button>
                </div>

                <!-- Mobile Menu Button -->
                <div class="-mr-2 flex md:hidden">
                    <button 
                        ref="menuButtonRef"
                        :aria-expanded="isMenuOpen"
                        :aria-controls="isMenuOpen ? 'mobile-menu' : undefined"
                        aria-haspopup="true"
                        aria-label="Abrir menú principal"
                        @click="toggleMenu"
                        @keydown="handleMenuButtonKeydown"
                        class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gold-500 transition"
                    >
                        <span class="sr-only">{{ isMenuOpen ? 'Cerrar menú' : 'Abrir menú' }}</span>
                        <!-- Icon Menu -->
                        <svg v-if="!isMenuOpen" class="block h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <!-- Icon Close -->
                        <svg v-else class="block h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Mobile Menu -->
        <div 
            v-show="isMenuOpen" 
            id="mobile-menu"
            role="menu"
            aria-label="Menú móvil"
            class="md:hidden bg-surface border-b border-white/5 absolute w-full backdrop-blur-xl"
        >
            <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                <button 
                    v-for="(link, index) in navLinks" 
                    :ref="index === 0 ? 'firstMenuItemRef' : undefined"
                    :key="link.name"
                    role="menuitem"
                    :aria-label="link.name"
                    @click="handleNavInfo(link.action || 'landing')"
                    @keydown.enter="handleNavInfo(link.action || 'landing')"
                    @keydown.space.prevent="handleNavInfo(link.action || 'landing')"
                    @keydown.down="(index + 1 < navLinks.length) && (index === navLinks.length - 1 ? null : null)"
                    class="text-gray-300 hover:text-white hover:bg-white/10 block px-3 py-4 rounded-md text-base font-bold w-full text-left transition focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-surface"
                >
                    {{ link.name }}
                </button>
                 <button
                    @click="handleNavInfo('my_tickets')" 
                    aria-label="Ver mis tickets"
                    class="w-full mt-4 py-4 rounded-xl bg-gold-500 text-black font-bold uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-surface"
                >
                    Mis Tickets
                </button>
            </div>
        </div>
    </nav>
</template>

<style scoped>
.text-gold-500 { color: #f59e0b; }
.text-gold-400 { color: #fbbf24; }
.bg-gold-500 { background-color: #f59e0b; }
.from-gold-400 { --tw-gradient-from: #fbbf24; }
.to-gold-600 { --tw-gradient-to: #d97706; }
</style>
